import { useMemo } from 'react';
import { Area, Rack, Shelf, Table, CheckoutZoneData } from '../../app/appContext';

interface FilteredCanvasData {
  cameraAreas: Area[];
  filteredRacks: Rack[];
  filteredShelves: Shelf[];
  filteredTables: Table[];
  filteredCheckoutZones: CheckoutZoneData[];
  standaloneAreas: Area[];
  areaMap: { [id: string]: Area };
}

export const useCanvasData = (
  areas: Area[],
  racks: Rack[],
  shelves: Shelf[],
  tables: Table[],
  checkoutZones: CheckoutZoneData[],
  selectedCameraId: string | null
): FilteredCanvasData => {
  const selectedCameraIdInt = selectedCameraId ? parseInt(selectedCameraId, 10) : null;

  return useMemo(() => {
    const areaMap = Object.fromEntries(areas.map(a => [String(a.id), a]));

    const cameraAreas = areas.filter(
      a => selectedCameraIdInt !== null &&
           a.camera !== undefined &&
           parseInt(String(a.camera), 10) === selectedCameraIdInt
    );

    // Normalize all IDs to strings for consistent comparison
    const cameraAreaIds = new Set(cameraAreas.map(a => String(a.id)));

    const filteredRacks = racks.filter(rack => {
      const area = areaMap[String(rack.area)];
      return area &&
             area.camera !== undefined &&
             selectedCameraIdInt !== null &&
             parseInt(String(area.camera), 10) === selectedCameraIdInt;
    });

    const filteredRackIds = new Set(filteredRacks.map(r => String(r.id)));
    const filteredShelves = shelves.filter(shelf => filteredRackIds.has(String(shelf.rack)));

    const filteredTables = tables.filter(table => {
      const area = areaMap[String(table.area)];
      return area &&
             area.camera !== undefined &&
             selectedCameraIdInt !== null &&
             parseInt(String(area.camera), 10) === selectedCameraIdInt;
    });

    // Filter checkout zones: those that have at least one area on this camera
    const filteredCheckoutZones = checkoutZones.filter(cz =>
      cz.areas.some(areaId => cameraAreaIds.has(String(areaId)))
    );

    // Collect all area IDs claimed by typed entities (all as strings)
    const filteredRackAreaIds = new Set(filteredRacks.map(r => String(r.area)));
    const filteredShelfAreaIds = new Set(filteredShelves.map(s => String(s.area)));
    const filteredTableAreaIds = new Set(filteredTables.map(t => String(t.area)));
    const checkoutZoneAreaIds = new Set(filteredCheckoutZones.flatMap(cz => cz.areas.map(String)));

    const standaloneAreas = cameraAreas.filter(
      area =>
        !filteredRackAreaIds.has(String(area.id)) &&
        !filteredShelfAreaIds.has(String(area.id)) &&
        !filteredTableAreaIds.has(String(area.id)) &&
        !checkoutZoneAreaIds.has(String(area.id))
    );

    return {
      cameraAreas,
      filteredRacks,
      filteredShelves,
      filteredTables,
      filteredCheckoutZones,
      standaloneAreas,
      areaMap
    };
  }, [areas, racks, shelves, tables, checkoutZones, selectedCameraIdInt]);
};
